import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';

import authAPI from 'app/auth';
import Layout from 'components/Layout';
import { defaultLoginRedirectFlow } from 'routes';
import { trackSegmentPageEvent } from 'app/utils';
import { useSelector } from 'react-redux';
import {
  cleanUserInfo,
  fetchBillingInfo,
  fetchUserInfo,
  selectBillingInfoFetched,
  selectDrawdownEnabled,
  selectUserInfo,
  selectUserInfoLoading,
  selectUserInfoLoadFailed,
} from 'app/store/user';
import { useAppDispatch, useAppSelector } from 'app/store';
import { isLegacyBilling } from 'lib/utils';
import { Routes } from 'config/constants';
import { cleanCheckoutRequests } from 'app/store/checkoutRequests';
import { env } from 'config/env';
import { shouldRequireEmailVerification } from 'app/emailVerification';
import { EmailVerificationGate } from 'components/EmailVerificationGate';
import { UserLoadError } from 'components/UserLoadError';
import { useFlags } from 'providers/FlagsProvider';
import { useAuth } from 'providers/AuthProvider';
import { canRead, canWrite } from 'app/access';

interface PrivateRouteProps {
  children: JSX.Element;
  fitted?: boolean;
  noLayout?: boolean;
}

const Loader = () => null;

export const PrivateRoute = ({
  children,
  fitted,
  noLayout,
}: PrivateRouteProps): JSX.Element | null => {
  const [user, loading] = useAuthState(authAPI.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { requireEmailVerification } = useFlags();

  const dispatch = useAppDispatch();
  const { logout } = useAuth();
  const loadingUserInfo = useSelector(selectUserInfoLoading);
  const hasUserInfo = Boolean(useSelector(selectUserInfo));
  const userInfo = useAppSelector(selectUserInfo);
  const userLoadFailed = useAppSelector(selectUserInfoLoadFailed);
  const drawdownEnabled = useAppSelector(selectDrawdownEnabled);
  const billingInfoFetched = useAppSelector(selectBillingInfoFetched);
  const locationPathname = useRef(location.pathname);
  const [, refreshEmailVerificationState] = useState(0);

  const [searchParams] = useSearchParams();
  const queryRedirect = searchParams.get('redirect');
  const allQueryParams = searchParams.toString();
  const requiresEmailVerification = shouldRequireEmailVerification(user, requireEmailVerification);
  const canReadBilling = canRead(userInfo, 'billing');
  const canWriteBilling = canWrite(userInfo, 'billing');

  useEffect(() => {
    trackSegmentPageEvent(location.pathname);
  }, [location]);

  // If someone was already logged in and opened a link directly to a dashboard page,
  // then our usual post-login fetch won't have fired. In this case we need to make
  // sure we load the user's info (which includes their API key) before we render
  // any dashboard content.
  //
  // Individual pages are written to fetch the specific data they need (e.g. the
  // inventory requests page will load inventory requests), but user info is a global
  // dependency that needs to be loaded before anything else.
  useEffect(() => {
    if (user && !requiresEmailVerification && !loadingUserInfo && !hasUserInfo && !userLoadFailed) {
      dispatch(fetchUserInfo(user.uid, user));
    }
  }, [dispatch, user, requiresEmailVerification, loadingUserInfo, hasUserInfo, userLoadFailed]);

  useEffect(() => {
    if (hasUserInfo && !requiresEmailVerification && canReadBilling) {
      dispatch(fetchBillingInfo());
    }
  }, [dispatch, hasUserInfo, requiresEmailVerification, canReadBilling]);

  const clearUserStore = () => {
    dispatch(cleanUserInfo());
    dispatch(cleanCheckoutRequests());
  };

  useEffect(() => {
    if (!loading && requiresEmailVerification) {
      return;
    }

    const fallback = () => {
      if (!user && queryRedirect) {
        clearUserStore();
        navigate(`${Routes.Login}?redirect=${queryRedirect}`);
        return;
      } else if (!user) {
        clearUserStore();
        if (
          location.pathname !== Routes.Login &&
          location.pathname !== Routes.Dashboard &&
          location.pathname !== Routes.Payment
        ) {
          searchParams.append('redirect', location.pathname);
        }
        navigate({
          pathname: Routes.Login,
          search: searchParams.toString(),
        });
        return;
      } else if (
        canWriteBilling &&
        !isLegacyBilling(userInfo?.billingPeriodSettings) &&
        !userInfo?.stripe?.paymentMethod &&
        env.VITE_NODE_ENV === 'production'
      ) {
        if (
          location.pathname !== Routes.Login &&
          location.pathname !== Routes.Dashboard &&
          location.pathname !== Routes.Payment &&
          location.pathname !== Routes.DrawdownSetup
        ) {
          locationPathname.current = location.pathname;
        }
        navigate(Routes.Payment, { state: { redirect: locationPathname.current } });
        return;
      } else if (
        canWriteBilling &&
        !isLegacyBilling(userInfo?.billingPeriodSettings) &&
        userInfo?.stripe?.paymentMethod &&
        drawdownEnabled === false &&
        !sessionStorage.getItem('drawdownSetupSkipped') &&
        location.pathname !== Routes.DrawdownSetup &&
        env.VITE_NODE_ENV !== 'staging'
      ) {
        navigate(Routes.DrawdownSetup);
        return;
      }
    };

    // Email verification intentionally happens before billing/payment/drawdown
    // redirects so unverified email/password users cannot enter protected
    // console flows while the gate is enabled.
    defaultLoginRedirectFlow(
      navigate,
      loading,
      user,
      queryRedirect,
      fallback,
      allQueryParams ? `?${allQueryParams}` : '',
    );
  }, [
    user,
    loading,
    navigate,
    queryRedirect,
    allQueryParams,
    requiresEmailVerification,
    userInfo?.stripe?.paymentMethod,
    userInfo?.billingPeriodSettings,
    canWriteBilling,
    drawdownEnabled,
  ]);

  const isSubscriptionBilling = userInfo && !isLegacyBilling(userInfo.billingPeriodSettings);
  const loadingBillingInfo = isSubscriptionBilling && canReadBilling && !billingInfoFetched;

  if (!loading && user && requiresEmailVerification) {
    return (
      <EmailVerificationGate
        user={user}
        onVerified={() => refreshEmailVerificationState((value) => value + 1)}
      />
    );
  }

  if (!loading && user && userLoadFailed && !hasUserInfo) {
    return (
      <Layout>
        <UserLoadError
          retrying={loadingUserInfo}
          onRetry={() => {
            dispatch(fetchUserInfo(user.uid, user));
          }}
          onSignOut={logout}
        />
      </Layout>
    );
  }

  return loading || (loadingUserInfo && !hasUserInfo) || queryRedirect || loadingBillingInfo ? (
    <Loader />
  ) : noLayout ? (
    <>{children}</>
  ) : (
    <Layout fitted={fitted}>{children}</Layout>
  );
};
